import { useState } from 'react';
import type { V2Evidence } from '../../domain/evidence';
import { useT } from '../../i18n/i18n';
import { importToPoolAction, removeEvidenceRecordAction } from '../../state/captureFlow';
import { ConfirmSheet, showToast } from '../../ui/components';

// Photo-pool import and delete, shared by the bulk import screen and the photo pool (Owner device test
// 2026-10-05):
//   - 「导入时会有一部分延迟，需增加导入状态栏，状态栏里需要有进度条和百分比」 — while the picked photos
//     are read and stored, a status bar shows how many are done, the percentage and a moving bar, so a
//     long import never looks like a frozen app;
//   - 「导入照片池的照片没有删除键，需要加」 — a photo can be deleted from the pool (one, or the
//     selection), always behind a confirmation. Only the app's record and its links go; the system
//     album copy is never touched (OWNER-CONTRACT §6.1.2 — removeEvidenceRecordAction has no album API).

// total 0 = the picker closed and the photos are still being read (the count is not known yet).
export interface ImportProgressState {
  done: number;
  total: number;
}

export interface PoolImport {
  busy: boolean;
  progress: ImportProgressState | null;
  message: { kind: 'ok' | 'err'; text: string } | null;
  run: () => Promise<V2Evidence[]>;
}

export function usePoolImport(inspectionId: string): PoolImport {
  const { tp } = useT();
  const [progress, setProgress] = useState<ImportProgressState | null>(null);
  const [message, setMessage] = useState<PoolImport['message']>(null);

  async function run(): Promise<V2Evidence[]> {
    setProgress({ done: 0, total: 0 });
    setMessage(null);
    try {
      const outcome = await importToPoolAction(inspectionId, Date.now(), (done, total) => setProgress({ done, total }));
      if (outcome.kind === 'error') {
        setMessage({ kind: 'err', text: outcome.message });
        return [];
      }
      if (outcome.kind === 'cancelled') return [];
      setMessage({ kind: 'ok', text: tp('import.imported', outcome.evidence.length) });
      return outcome.evidence;
    } finally {
      setProgress(null);
    }
  }

  return { busy: progress !== null, progress, message, run };
}

export function ImportProgressBar(props: { progress: ImportProgressState }) {
  const { t } = useT();
  const { done, total } = props.progress;
  const reading = total === 0;
  const pct = reading ? 0 : Math.min(100, Math.round((done / total) * 100));
  return (
    <div className="import-progress" role="status" aria-live="polite" data-testid="import-progress" data-phase={reading ? 'reading' : 'saving'} data-done={done} data-total={total}>
      <div className="import-progress-head">
        <span className="ai-progress-spinner" aria-hidden="true" />
        <span className="import-progress-title" data-testid="import-progress-title">
          {reading ? t('import.reading') : t('import.progress', { done, total })}
        </span>
        {/* Issue 21: while the picked files are still being read there is no count yet — the moving stripe
            says "working"; a 「0%」 next to it read as stuck. */}
        {reading ? null : (
          <span className="import-progress-pct" data-testid="import-progress-pct">
            {t('common.percent', { n: pct })}
          </span>
        )}
      </div>
      <div className={`import-progress-track ${reading ? 'import-progress-track-waiting' : ''}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="import-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="import-progress-hint">{t('import.progressHint')}</p>
    </div>
  );
}

// Delete one or several pool photos. `linked` = how many of them are linked to an item or a finding:
// the confirmation says those links go too.
export function DeletePhotosConfirm(props: { ids: string[]; linked: number; onClose: () => void; onDeleted?: (count: number) => void }) {
  const { t, tp } = useT();
  const count = props.ids.length;
  const body = props.linked > 0 ? `${t('photo.deleteBody')} ${tp('pool.deleteLinkedNote', props.linked)}` : t('photo.deleteBody');

  async function confirm() {
    const ids = [...props.ids];
    props.onClose();
    let deleted = 0;
    for (const id of ids) {
      await removeEvidenceRecordAction(id);
      deleted += 1;
    }
    showToast(tp('pool.deletedToast', deleted));
    props.onDeleted?.(deleted);
  }

  return (
    <ConfirmSheet
      title={count === 1 ? t('photo.deleteTitle') : t('pool.deleteManyTitle', { n: count })}
      body={body}
      confirmLabel={t('photo.deleteConfirm')}
      danger
      onConfirm={() => void confirm()}
      onCancel={props.onClose}
      testId="pool-delete-confirm"
    />
  );
}
